import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { IconType } from "react-icons";
import {
  FiAlertTriangle, FiCheck, FiCheckCircle, FiClock, FiCopy, FiKey, FiLock, FiLogOut, FiMail, FiMinusCircle,
  FiMoreHorizontal, FiSearch, FiShield, FiSlash, FiUserCheck, FiUserPlus, FiUsers, FiX,
} from "react-icons/fi";

import {
  accessAudit, createInvite, disableUser, graphTargets, listInvites, listUsers, revokeInvite, revokeSessions,
  setUserGraphPorts, updateUser, userGraphPorts, type GraphTargetDevice,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { AccessEvent, Invite, PanelUser, Role } from "@/lib/types";
import {
  accountState, blockDisable, blockReset, blockRoleChange, isEscalation, isExternal, PERMS, ROLES, roleDiff,
  type AccountState, type Block, type Grant,
} from "@/lib/access";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PasswordDialog from "@/components/PasswordDialog";
import { useNow } from "@/components/MonitoringBanner";
import { durText } from "@/components/OntStatus";
import { fmtDateTime, fmtNum, useI18n, type Key } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type T = (k: Key, p?: Record<string, string | number>) => string;
type Tab = "users" | "roles" | "invites" | "audit";

const sessionsText = (n: number, t: T) => t(n === 0 ? "ua.sec.noSessions" : n === 1 ? "ua.sec.oneSession" : "ua.sec.sessions", { n });
const roleName = (r: Role | string, t: T) => t(`ua.role.${r}` as Key);
const ago = (iso: string, now: number, t: T) => t("ua.ago", { age: durText(now - Date.parse(iso)) });

// ---------- status ----------

type Status = AccountState | "invited" | "expired";
const STATUS_UI: Record<Status, { icon: IconType; text: string; bg: string }> = {
  active: { icon: FiCheckCircle, text: "text-signal-good", bg: "bg-signal-good/10" },
  inactive: { icon: FiClock, text: "text-signal-warn", bg: "bg-amber-400/10" },
  disabled: { icon: FiMinusCircle, text: "text-slate-400", bg: "bg-slate-700/50" },
  invited: { icon: FiMail, text: "text-signal", bg: "bg-signal/10" },
  expired: { icon: FiSlash, text: "text-slate-400", bg: "bg-slate-700/40" },
};

function StatusBadge({ s }: { s: Status }) {
  const { t } = useI18n();
  const u = STATUS_UI[s];
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold tracking-[0.12em] uppercase", u.text, u.bg)}>
      <u.icon className="size-3" /> {t(`ua.status.${s}` as Key)}
    </span>
  );
}

// Roles are neutral: colour is kept for state, not privilege.
const RoleTag = ({ r }: { r: Role }) => {
  const { t } = useI18n();
  return <span className="rounded-sm border border-rule px-1.5 py-px text-[11px] font-semibold tracking-[0.08em] text-slate-200 uppercase">{roleName(r, t)}</span>;
};

const Tag = ({ children, tone = "slate" }: { children: ReactNode; tone?: "slate" | "signal" | "amber" }) => (
  <span className={cn("rounded-sm px-1 py-px text-[9.5px] font-semibold tracking-[0.14em] uppercase",
    tone === "signal" ? "bg-signal/15 text-signal" : tone === "amber" ? "bg-amber-400/15 text-amber-200" : "bg-slate-700/60 text-slate-300")}>{children}</span>
);

// ---------- page ----------

export default function UsersPage() {
  const { t, locale } = useI18n();
  const { user: me } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = (["users", "roles", "invites", "audit"].includes(sp.get("tab") ?? "") ? sp.get("tab") : "users") as Tab;
  const now = useNow(30_000);
  const users = useQuery({ queryKey: ["users"], queryFn: listUsers, refetchInterval: 60_000 });
  const invites = useQuery({ queryKey: ["invites"], queryFn: listInvites, refetchInterval: 60_000 });
  const [inviting, setInviting] = useState<{ email?: string; role?: Role } | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  if (me && me.role !== "admin") return <Navigate to="/" replace />;
  const list = users.data?.users ?? [];
  const pending = (invites.data?.invites ?? []).filter((i) => i.status === "pending");
  const active = list.filter((u) => !u.disabled_at);
  const n = (v: number) => fmtNum(locale, v);
  const inactive = active.filter((u) => accountState(u, now) === "inactive").length;
  const summary = [
    t("ua.sum.active", { n: n(active.length - inactive) }),
    ...(inactive ? [t("ua.sum.inactive", { n: n(inactive) })] : []),
    ...ROLES.filter((r) => r !== "portviewer").map((r) => ({ r, c: active.filter((u) => u.role === r).length })).filter((x) => x.c).map((x) => `${n(x.c)} ${roleName(x.r, t)}`),
    t("ua.sum.external", { n: n(active.filter((u) => isExternal(u.role)).length) }),
    t("ua.sum.pending", { n: n(pending.length) }),
  ];
  const setTab = (v: Tab) => { const p = new URLSearchParams(sp); if (v === "users") p.delete("tab"); else p.set("tab", v); setSp(p, { replace: true }); };
  const open = list.find((u) => u.id === openId) ?? null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">{t("ua.title")}</h1>
          <p className="data mt-1 text-[12.5px] text-slate-400">{summary.join("  ·  ")}</p>
        </div>
        <Button size="sm" onClick={() => setInviting({})}><FiUserPlus /> {t("ua.invite")}</Button>
      </div>

      <div role="tablist" className="flex w-fit rounded-sm border border-rule p-0.5">
        {(["users", "roles", "invites", "audit"] as Tab[]).map((v) => (
          <button key={v} role="tab" type="button" aria-selected={tab === v} onClick={() => setTab(v)}
            className={cn("rounded-sm px-3 py-1 text-[12.5px]", tab === v ? "bg-signal/10 text-slate-100" : "text-slate-400 hover:text-slate-100")}>
            {t(`ua.tab.${v}` as Key)}
            {v === "invites" && pending.length > 0 && <span className="data ml-1.5 text-signal">{pending.length}</span>}
          </button>
        ))}
      </div>

      {(users.error || invites.error) && <p className="text-signal-crit">{((users.error || invites.error) as Error).message}</p>}
      {users.isLoading && <p className="text-slate-400">{t("common.loading")}</p>}

      {users.data && me && tab === "users" && (
        <UsersTab users={list} invites={invites.data?.invites ?? []} me={me} now={now} onOpen={setOpenId} onInvites={() => setTab("invites")} />
      )}
      {tab === "roles" && <RolesTab users={list} />}
      {tab === "invites" && <InvitesTab invites={invites.data?.invites ?? []} now={now} onResend={(i) => setInviting({ email: i.email, role: i.role })} />}
      {tab === "audit" && <AuditTab users={list} now={now} />}

      {open && me && <UserDrawer u={open} users={list} me={me} now={now} onClose={() => setOpenId(null)} />}
      {inviting && <InviteDialog initial={inviting} onClose={() => setInviting(null)} />}
    </div>
  );
}

// ---------- users tab ----------

type Quick = "all" | "admin" | "noc" | "external" | "disabled" | "never" | "inactive";
const PAGE = 50;

function UsersTab({ users, invites, me, now, onOpen, onInvites }: {
  users: PanelUser[]; invites: Invite[]; me: { id: number; is_superadmin?: boolean }; now: number; onOpen: (id: number) => void; onInvites: () => void;
}) {
  const { t, locale } = useI18n();
  const [q, setQ] = useState("");
  const [quick, setQuick] = useState<Quick>("all");
  const [page, setPage] = useState(0);
  const pending = invites.filter((i) => i.status === "pending");
  const test = (u: PanelUser, f: Quick) => {
    const s = accountState(u, now);
    switch (f) {
      case "admin": return u.role === "admin";
      case "noc": return u.role === "noc";
      case "external": return isExternal(u.role);
      case "disabled": return s === "disabled";
      case "never": return !u.last_login_at && !u.last_active_at;
      case "inactive": return s === "inactive";
      default: return true;
    }
  };
  const needle = q.trim().toLowerCase();
  // Most privileged and most in need of review first; disabled last.
  const rank: Record<AccountState, number> = { inactive: 0, active: 1, disabled: 2 };
  const rows = users.filter((u) => test(u, quick) && (!needle || [u.email, u.role, ...(u.ports ?? [])].some((v) => v.toLowerCase().includes(needle))))
    .sort((a, b) => rank[accountState(a, now)] - rank[accountState(b, now)] || ROLES.indexOf(a.role) - ROLES.indexOf(b.role) || a.email.localeCompare(b.email));
  const inviteRows = quick === "all" ? pending.filter((i) => !needle || i.email.toLowerCase().includes(needle)) : [];
  const count = (f: Quick) => users.filter((u) => test(u, f)).length;
  const shown = rows.slice(page * PAGE, (page + 1) * PAGE);
  useEffect(() => setPage(0), [q, quick]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-72">
          <FiSearch className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ua.search")} aria-label={t("ua.search")} className="h-8 border-rule bg-panel pl-8 text-[12.5px]" />
        </div>
        {(["all", "admin", "noc", "external", "inactive", "never", "disabled"] as Quick[]).map((f) => {
          const c = count(f);
          if (f !== "all" && !c) return null;
          return (
            <button key={f} type="button" aria-pressed={quick === f} onClick={() => setQuick(f)}
              className={cn("rounded-sm border px-2 py-1 text-[12px]", quick === f ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>
              {t(`ua.quick.${f}` as Key)} <span className={cn("data", f === "inactive" ? "text-signal-warn" : "text-slate-500")}>{fmtNum(locale, c)}</span>
            </button>
          );
        })}
      </div>

      <div className="overflow-x-auto rounded-lg border border-rule bg-panel">
        <table className="w-full min-w-[1040px] table-fixed text-[13px]">
          <thead>
            <tr className="border-b border-rule text-left">
              {(["ua.col.user", "ua.col.role", "ua.col.scope", "ua.col.status", "ua.col.lastActive", "ua.col.security"] as Key[]).map((k, i) => (
                <th key={k} className={cn("eyebrow px-4 py-2.5 font-medium", ["w-[25%]", "w-[19%]", "w-[16%]", "w-[10%]", "w-[13%]", "w-[13%]"][i])}>{t(k)}</th>
              ))}
              <th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {shown.map((u) => <UserRow key={u.id} u={u} users={users} me={me} now={now} onOpen={() => onOpen(u.id)} />)}
            {inviteRows.map((i) => (
              <tr key={`inv-${i.id}`} className="border-b border-rule/60 last:border-b-0 text-slate-400">
                <td className="px-4 py-2.5"><div className="truncate text-slate-300">{i.email}</div><div className="text-[11.5px] text-slate-500">{t("ua.invitedAgo", { age: durText(now - Date.parse(i.created_at)) })}</div></td>
                <td className="px-4 py-2.5"><RoleTag r={i.role} /></td>
                <td className="px-4 py-2.5 text-[12.5px] text-slate-500">—</td>
                <td className="px-4 py-2.5"><StatusBadge s="invited" /></td>
                <td className="px-4 py-2.5 text-[12.5px]">{t("ua.expiresIn", { age: durText(Date.parse(i.expires_at) - now) })}</td>
                <td className="px-4 py-2.5 text-[12.5px] text-slate-500">{t("ua.sec.invite")}</td>
                <td className="pr-3 text-right"><button type="button" onClick={onInvites} className="text-[12px] text-slate-400 hover:text-signal">{t("ua.manage")}</button></td>
              </tr>
            ))}
            {rows.length === 0 && inviteRows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">{t("ua.noMatch")}</td></tr>}
          </tbody>
        </table>
      </div>
      {rows.length > PAGE && (
        <div className="flex items-center justify-end gap-2 text-[12px] text-slate-400">
          <span className="data">{page * PAGE + 1}–{Math.min(rows.length, (page + 1) * PAGE)} / {rows.length}</span>
          <Button size="xs" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>‹</Button>
          <Button size="xs" variant="outline" disabled={(page + 1) * PAGE >= rows.length} onClick={() => setPage(page + 1)}>›</Button>
        </div>
      )}
      {pending.length === 0 && quick === "all" && <p className="text-[12px] text-slate-500">{t("ua.noPending")}</p>}
    </div>
  );
}

function Scope({ u, compact }: { u: PanelUser; compact?: boolean }) {
  const { t } = useI18n();
  if (u.role === "portviewer") {
    const ports = u.ports ?? [];
    if (!ports.length) return <span className="text-signal-warn">{t("ua.scope.none")}</span>;
    return (
      <span title={ports.join("\n")}>
        {t("ua.scope.ports", { n: ports.length })}
        {!compact && <span className="block truncate text-[11.5px] text-slate-500">{ports.join(" · ")}</span>}
      </span>
    );
  }
  return <span>{t(`ua.scope.${u.role}` as Key)}</span>;
}

function UserRow({ u, users, me, now, onOpen }: { u: PanelUser; users: PanelUser[]; me: { id: number; is_superadmin?: boolean }; now: number; onOpen: () => void }) {
  const { t, locale } = useI18n();
  const s = accountState(u, now);
  const last = u.last_active_at ?? u.last_login_at;
  return (
    <tr onClick={onOpen} className={cn("group cursor-pointer border-b border-rule/60 last:border-b-0 hover:bg-white/[0.02]", s === "disabled" && "opacity-60")}>
      <td className="px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate font-medium text-slate-100 group-hover:text-signal">{u.email}</span>
          {u.id === me.id && <Tag tone="signal">{t("ua.you")}</Tag>}
          {u.is_superadmin && <Tag>{t("ua.owner")}</Tag>}
          {isExternal(u.role) && <Tag tone="amber">{t("ua.external")}</Tag>}
        </div>
      </td>
      <td className="px-4 py-2.5">
        <RoleTag r={u.role} />
        <div className="mt-0.5 truncate text-[11.5px] text-slate-500">{t(`ua.roleDesc.${u.role}` as Key)}</div>
      </td>
      <td className="px-4 py-2.5 text-[12.5px] text-slate-300"><Scope u={u} /></td>
      <td className="px-4 py-2.5"><StatusBadge s={s} /></td>
      <td className="px-4 py-2.5 text-[12.5px]">
        {last ? <span className={s === "inactive" ? "text-signal-warn" : "text-slate-200"} title={fmtDateTime(locale, last)}>{ago(last, now, t)}</span>
          : <span className="text-slate-400">{t("ua.never")}</span>}
        {s === "inactive" && <div className="text-[11px] text-signal-warn/80">{t("ua.review")}</div>}
      </td>
      <td className="px-4 py-2.5 text-[12px] text-slate-400">
        <div>{t(u.password_set ? "ua.sec.password" : "ua.sec.noPassword")}</div>
        <div className={u.sessions ? "text-slate-300" : "text-slate-500"}>{sessionsText(u.sessions, t)}</div>
      </td>
      <td className="pr-3 text-right" onClick={(e) => e.stopPropagation()}>
        <ActionMenu u={u} users={users} me={me} onOpen={onOpen} />
      </td>
    </tr>
  );
}

// ---------- actions ----------

type Dlg = "role" | "disable" | "enable" | "sessions" | "password" | "graphs" | null;

function blockText(b: Block, t: T) { return b ? t(`ua.block.${b}` as Key) : undefined; }

function ActionMenu({ u, users, me, onOpen }: { u: PanelUser; users: PanelUser[]; me: { id: number; is_superadmin?: boolean }; onOpen: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [dlg, setDlg] = useState<Dlg>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", close); };
  }, [open]);
  const roleB = blockRoleChange(u, me, users);
  const disB = blockDisable(u, me, users);
  const resB = blockReset(u, me);
  const item = (icon: IconType, label: string, onClick: () => void, opts: { block?: Block; danger?: boolean } = {}) => {
    const I = icon;
    return (
      <button type="button" role="menuitem" disabled={!!opts.block} title={blockText(opts.block ?? null, t)}
        onClick={() => { setOpen(false); onClick(); }}
        className={cn("flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] disabled:cursor-not-allowed disabled:opacity-40",
          opts.danger ? "text-rose-300 hover:bg-signal-crit/10" : "text-slate-200 hover:bg-white/[0.05]")}>
        <I className="size-3.5 shrink-0" /> <span className="flex-1">{label}</span>
      </button>
    );
  };
  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={t("ua.actions", { email: u.email })} onClick={() => setOpen(!open)}
        className="grid size-7 place-items-center rounded-sm text-slate-400 hover:bg-white/5 hover:text-slate-100">
        <FiMoreHorizontal />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-1 w-56 overflow-hidden rounded-md border border-rule bg-[#070b14] py-1 text-left shadow-2xl">
          {item(FiUsers, t("ua.act.view"), onOpen)}
          {!u.disabled_at && item(FiShield, t("ua.act.role"), () => setDlg("role"), { block: roleB })}
          {u.role === "portviewer" && item(FiCheck, t("ua.act.graphs"), () => setDlg("graphs"))}
          <div className="my-1 border-t border-rule" />
          <p className="px-3 pt-0.5 pb-1 text-[10px] tracking-[0.14em] text-slate-500 uppercase">{t("ua.act.security")}</p>
          {item(FiKey, t("ua.act.reset"), () => setDlg("password"), { block: resB })}
          {item(FiLogOut, t("ua.act.sessions"), () => setDlg("sessions"))}
          <div className="my-1 border-t border-rule" />
          {u.disabled_at
            ? item(FiUserCheck, t("ua.act.enable"), () => setDlg("enable"))
            : item(FiLock, t("ua.act.disable"), () => setDlg("disable"), { block: disB, danger: true })}
        </div>
      )}
      <Dialogs u={u} dlg={dlg} onClose={() => setDlg(null)} />
    </div>
  );
}

function Dialogs({ u, dlg, onClose }: { u: PanelUser; dlg: Dlg; onClose: () => void }) {
  if (dlg === "role") return <RoleDialog u={u} onClose={onClose} />;
  if (dlg === "disable" || dlg === "enable") return <StatusDialog u={u} disable={dlg === "disable"} onClose={onClose} />;
  if (dlg === "sessions") return <SessionsDialog u={u} onClose={onClose} />;
  if (dlg === "password") return <PasswordDialog target={{ id: u.id, email: u.email }} onClose={onClose} />;
  if (dlg === "graphs") return <GraphAccessDialog user={u} onClose={onClose} />;
  return null;
}

function useUserMutation<A>(fn: (a: A) => Promise<unknown>, onDone: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["access-audit"] });
      onDone();
    },
  });
}

function RoleDialog({ u, onClose }: { u: PanelUser; onClose: () => void }) {
  const { t } = useI18n();
  const [to, setTo] = useState<Role>(u.role);
  const save = useUserMutation(() => updateUser(u.id, { role: to }), onClose);
  const diff = roleDiff(u.role, to);
  const up = to !== u.role && isEscalation(u.role, to);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("ua.roleTitle")}</DialogTitle>
          <DialogDescription className="data">{u.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          {ROLES.map((r) => (
            <label key={r} className={cn("flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2", to === r ? "border-signal/50 bg-signal/[0.06]" : "border-rule hover:border-slate-600")}>
              <input type="radio" name="role" className="mt-1 accent-cyan-400" checked={to === r} onChange={() => setTo(r)} />
              <span>
                <span className="text-[13px] font-medium text-slate-100">{roleName(r, t)}</span>
                {r === u.role && <span className="ml-2 text-[11px] text-slate-500">{t("ua.current")}</span>}
                <span className="block text-[12px] text-slate-400">{t(`ua.roleDesc.${r}` as Key)}</span>
              </span>
            </label>
          ))}
        </div>
        {to !== u.role && (
          <div className={cn("rounded-md border px-3 py-2.5 text-[12.5px]", up ? "border-amber-400/40 bg-amber-400/[0.06]" : "border-rule")}>
            <p className="font-medium text-slate-100">
              {roleName(u.role, t)} <span className="text-slate-500">→</span> {roleName(to, t)}
            </p>
            {diff.gains.length > 0 && (
              <>
                <p className="mt-1.5 text-[11px] tracking-[0.12em] text-amber-200 uppercase">{t("ua.grants")}</p>
                <ul className="mt-0.5 space-y-px text-slate-200">{diff.gains.map((k) => <li key={k}>+ {t(`ua.perm.${k}` as Key)}</li>)}</ul>
              </>
            )}
            {diff.losses.length > 0 && (
              <>
                <p className="mt-1.5 text-[11px] tracking-[0.12em] text-slate-400 uppercase">{t("ua.removes")}</p>
                <ul className="mt-0.5 space-y-px text-slate-400">{diff.losses.map((k) => <li key={k}>− {t(`ua.perm.${k}` as Key)}</li>)}</ul>
              </>
            )}
            <p className="mt-2 text-[11.5px] text-slate-500">{t("ua.roleSessions")}</p>
          </div>
        )}
        {save.error && <p className="text-[12.5px] text-signal-crit">{(save.error as Error).message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button disabled={to === u.role || save.isPending} onClick={() => save.mutate(undefined)}
            className={up && to === "admin" ? "bg-amber-500 text-slate-950 hover:bg-amber-400" : undefined}>
            {up && to === "admin" ? t("ua.grantAdmin") : t("ua.changeRole")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusDialog({ u, disable, onClose }: { u: PanelUser; disable: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const save = useUserMutation(() => (disable ? disableUser(u.id) : updateUser(u.id, { disabled: false })), onClose);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t(disable ? "ua.disableTitle" : "ua.enableTitle")}</DialogTitle>
          <DialogDescription className="data">{u.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-[13px] text-slate-300">
          {disable ? (
            <>
              <p>{t("ua.disableBody")}</p>
              <p className="text-slate-400">{t("ua.disableSessions", { n: u.sessions })}</p>
              <p className="text-[12px] text-slate-500">{t("ua.noDelete")}</p>
            </>
          ) : <p>{t("ua.enableBody", { role: roleName(u.role, t) })}</p>}
        </div>
        {save.error && <p className="text-[12.5px] text-signal-crit">{(save.error as Error).message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button disabled={save.isPending} onClick={() => save.mutate(undefined)} variant={disable ? "destructive" : "default"}>
            {t(disable ? "ua.act.disable" : "ua.act.enable")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SessionsDialog({ u, onClose }: { u: PanelUser; onClose: () => void }) {
  const { t } = useI18n();
  const [done, setDone] = useState<number | null>(null);
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => revokeSessions(u.id),
    onSuccess: (r) => { setDone(r.revoked); qc.invalidateQueries({ queryKey: ["users"] }); qc.invalidateQueries({ queryKey: ["access-audit"] }); },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("ua.sessionsTitle")}</DialogTitle>
          <DialogDescription className="data">{u.email}</DialogDescription>
        </DialogHeader>
        {done === null ? (
          <div className="space-y-2 text-[13px] text-slate-300">
            <p>{t("ua.sessionsBody", { n: u.sessions })}</p>
            <p className="text-[12px] text-slate-500">{t("ua.sessionsNote")}</p>
          </div>
        ) : <p className="text-[13px] text-signal-good">{t("ua.sessionsDone", { n: done })}</p>}
        {save.error && <p className="text-[12.5px] text-signal-crit">{(save.error as Error).message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t(done === null ? "common.cancel" : "common.close")}</Button>
          {done === null && <Button disabled={save.isPending} onClick={() => save.mutate()}><FiLogOut /> {t("ua.act.sessions")}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- drawer ----------

function UserDrawer({ u, users, me, now, onClose }: { u: PanelUser; users: PanelUser[]; me: { id: number; is_superadmin?: boolean }; now: number; onClose: () => void }) {
  const { t, locale } = useI18n();
  const [dlg, setDlg] = useState<Dlg>(null);
  const events = useQuery({ queryKey: ["access-audit", "user", u.id], queryFn: () => accessAudit({ user: u.id, limit: 12 }) });
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && !dlg && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose, dlg]);
  const s = accountState(u, now);
  const last = u.last_active_at ?? u.last_login_at;
  const roleB = blockRoleChange(u, me, users), disB = blockDisable(u, me, users), resB = blockReset(u, me);
  const btn = "inline-flex items-center gap-1.5 rounded-sm border border-rule px-2.5 py-1 text-[12px] text-slate-200 hover:border-signal/50 hover:text-signal disabled:cursor-not-allowed disabled:opacity-40";
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/50" onClick={onClose}>
      <aside role="dialog" aria-modal aria-label={u.email} onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-[560px] flex-col overflow-y-auto border-l border-rule bg-[#070b14]">
        <header className="border-b border-rule px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="data truncate text-[15px] font-semibold text-slate-100">{u.email}</h2>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <StatusBadge s={s} /><RoleTag r={u.role} />
                {u.id === me.id && <Tag tone="signal">{t("ua.you")}</Tag>}
                {u.is_superadmin && <Tag>{t("ua.owner")}</Tag>}
                {isExternal(u.role) && <Tag tone="amber">{t("ua.external")}</Tag>}
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label={t("common.close")} className="p-1 text-slate-400 hover:text-slate-100"><FiX className="size-4" /></button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {!u.disabled_at && <button type="button" className={btn} disabled={!!roleB} title={blockText(roleB, t)} onClick={() => setDlg("role")}><FiShield /> {t("ua.act.role")}</button>}
            {u.role === "portviewer" && <button type="button" className={btn} onClick={() => setDlg("graphs")}><FiCheck /> {t("ua.act.graphs")}</button>}
            <button type="button" className={btn} disabled={!!resB} title={blockText(resB, t)} onClick={() => setDlg("password")}><FiKey /> {t("ua.act.reset")}</button>
            <button type="button" className={btn} onClick={() => setDlg("sessions")}><FiLogOut /> {t("ua.act.sessions")}</button>
            {u.disabled_at
              ? <button type="button" className={btn} onClick={() => setDlg("enable")}><FiUserCheck /> {t("ua.act.enable")}</button>
              : <button type="button" className={cn(btn, "border-signal-crit/40 text-rose-300 hover:border-signal-crit hover:text-rose-200")} disabled={!!disB} title={blockText(disB, t)} onClick={() => setDlg("disable")}><FiLock /> {t("ua.act.disable")}</button>}
          </div>
          {(roleB === "lastAdmin" || disB === "lastAdmin") && <p className="mt-2 flex items-center gap-1.5 text-[12px] text-amber-200"><FiAlertTriangle /> {t("ua.block.lastAdmin")}</p>}
          {(roleB === "self" || disB === "self") && <p className="mt-2 text-[12px] text-slate-500">{t("ua.selfNote")}</p>}
        </header>
        <div className="space-y-4 px-5 py-4 text-[13px]">
          <Sec title={t("ua.sec.access")}>
            <KV k={t("ua.col.role")}>{roleName(u.role, t)} <span className="text-slate-500">· {t(`ua.roleDesc.${u.role}` as Key)}</span></KV>
            <KV k={t("ua.col.scope")}><Scope u={u} /></KV>
          </Sec>
          <Sec title={t("ua.sec.activity")}>
            <KV k={t("ua.col.lastActive")}>{last ? <span title={fmtDateTime(locale, last)}>{ago(last, now, t)}</span> : t("ua.never")}</KV>
            <KV k={t("ua.lastLogin")}>{u.last_login_at ? <><span title={fmtDateTime(locale, u.last_login_at)}>{ago(u.last_login_at, now, t)}</span>{u.last_login_ip && <span className="data text-slate-500"> · {u.last_login_ip}</span>}</> : t("ua.never")}</KV>
            <KV k={t("ua.created")}>{fmtDateTime(locale, u.created_at)}</KV>
            {u.disabled_at && <KV k={t("ua.disabledAt")}>{fmtDateTime(locale, u.disabled_at)}</KV>}
            {s === "inactive" && <p className="text-[12px] text-signal-warn">{t("ua.inactiveNote", { days: 90 })}</p>}
          </Sec>
          <Sec title={t("ua.sec.security")}>
            <KV k={t("ua.password")}>{t(u.password_set ? "ua.sec.password" : "ua.sec.noPassword")}</KV>
            <KV k="MFA"><span className="text-slate-500">{t("ua.mfaNone")}</span></KV>
            <KV k={t("ua.sessionsLabel")}>{sessionsText(u.sessions, t)}</KV>
          </Sec>
          <Sec title={t("ua.sec.recent")}>
            {events.isLoading && <p className="text-slate-500">{t("common.loading")}</p>}
            {events.data && events.data.events.length === 0 && <p className="text-slate-500">{t("ua.audit.none")}</p>}
            <ul className="space-y-1.5">
              {events.data?.events.map((e) => (
                <li key={e.id} className="flex gap-3 text-[12.5px]">
                  <span className="data w-16 shrink-0 text-slate-500" title={fmtDateTime(locale, e.at)}>{durText(now - Date.parse(e.at))}</span>
                  <span className="text-slate-300"><EventText e={e} /></span>
                </li>
              ))}
            </ul>
          </Sec>
          <p className="text-[11.5px] text-slate-500">{t("ua.noDelete")}</p>
        </div>
        <Dialogs u={u} dlg={dlg} onClose={() => setDlg(null)} />
      </aside>
    </div>
  );
}

const Sec = ({ title, children }: { title: string; children: ReactNode }) => (
  <section><h3 className="eyebrow mb-1.5">{title}</h3><div className="space-y-1">{children}</div></section>
);
const KV = ({ k, children }: { k: string; children: ReactNode }) => (
  <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-2 border-b border-rule/50 pb-1 text-[12.5px] last:border-b-0">
    <span className="text-slate-500">{k}</span><span className="min-w-0 text-slate-200">{children}</span>
  </div>
);

// ---------- roles tab ----------

function Cell({ g }: { g: Grant }) {
  const { t } = useI18n();
  if (g === true) return <FiCheck className="mx-auto size-4 text-signal-good" aria-label={t("ua.yes")} />;
  if (g === false) return <span className="text-slate-600" aria-label={t("ua.no")}>—</span>;
  return <span className="text-[11.5px] text-amber-200/90">{t(`ua.grant.${g}` as Key)}</span>;
}

function RolesTab({ users }: { users: PanelUser[] }) {
  const { t, locale } = useI18n();
  const groups = ["monitor", "operate", "configure", "access"] as const;
  return (
    <div className="space-y-2">
      <p className="text-[12.5px] text-slate-400">{t("ua.rolesIntro")}</p>
      <div className="overflow-x-auto rounded-lg border border-rule bg-panel">
        <table className="w-full min-w-[900px] table-fixed text-[13px]">
          <thead>
            <tr className="border-b border-rule text-left">
              <th className="eyebrow w-[36%] px-4 py-2.5 font-medium">{t("ua.permission")}</th>
              {ROLES.map((r) => (
                <th key={r} className="px-3 py-2.5 text-center align-top font-normal">
                  <div className="text-[12px] font-semibold tracking-[0.1em] text-slate-100 uppercase">{roleName(r, t)}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{t(`ua.roleDesc.${r}` as Key)}</div>
                  <div className="data mt-0.5 text-[11px] text-slate-400">{(() => { const c = users.filter((u) => u.role === r && !u.disabled_at).length; return t(c === 1 ? "ua.member" : "ua.members", { n: fmtNum(locale, c) }); })()}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <PermGroup key={g} g={g} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PermGroup({ g }: { g: "monitor" | "operate" | "configure" | "access" }) {
  const { t } = useI18n();
  return (
    <>
      <tr className="border-b border-rule/60 bg-white/[0.015]"><td colSpan={5} className="eyebrow px-4 py-1.5 text-[10px]">{t(`ua.group.${g}` as Key)}</td></tr>
      {PERMS.filter((p) => p.group === g).map((p) => (
        <tr key={p.key} className="border-b border-rule/40 last:border-b-0">
          <td className="px-4 py-1.5 text-slate-200">{t(`ua.perm.${p.key}` as Key)}</td>
          {ROLES.map((r) => <td key={r} className="px-3 py-1.5 text-center"><Cell g={p.grant[r]} /></td>)}
        </tr>
      ))}
    </>
  );
}

// ---------- invitations ----------

function InvitesTab({ invites, now, onResend }: { invites: Invite[]; now: number; onResend: (i: Invite) => void }) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const revoke = useMutation({ mutationFn: revokeInvite, onSuccess: () => { qc.invalidateQueries({ queryKey: ["invites"] }); qc.invalidateQueries({ queryKey: ["access-audit"] }); } });
  const open = invites.filter((i) => i.status !== "accepted");
  const accepted = invites.filter((i) => i.status === "accepted");
  const [history, setHistory] = useState(false);
  return (
    <div className="space-y-3">
      <p className="text-[12.5px] text-slate-400">{t("ua.inviteNote")}</p>
      <section className="overflow-hidden rounded-lg border border-rule bg-panel">
        <h2 className="eyebrow border-b border-rule px-4 py-2">{t("ua.inv.open")} <span className="data text-slate-500">{open.length}</span></h2>
        {open.length === 0 && <p className="px-4 py-6 text-center text-[13px] text-slate-500">{t("ua.inv.none")}</p>}
        <table className="w-full table-fixed text-[13px]">
          <tbody>
            {open.map((i) => (
              <tr key={i.id} className="border-b border-rule/50 last:border-b-0">
                <td className="w-[32%] truncate px-4 py-2.5 text-slate-100">{i.email}</td>
                <td className="w-[14%] px-3 py-2.5"><RoleTag r={i.role} /></td>
                <td className="w-[12%] px-3 py-2.5"><StatusBadge s={i.status === "expired" ? "expired" : "invited"} /></td>
                <td className="w-[14%] px-3 py-2.5 text-[12.5px] text-slate-400" title={fmtDateTime(locale, i.created_at)}>{t("ua.invitedAgo", { age: durText(now - Date.parse(i.created_at)) })}</td>
                <td className="w-[14%] px-3 py-2.5 text-[12.5px]" title={fmtDateTime(locale, i.expires_at)}>
                  {i.status === "expired" ? <span className="text-slate-500">{t("ua.expiredAgo", { age: durText(now - Date.parse(i.expires_at)) })}</span>
                    : <span className="text-slate-300">{t("ua.expiresIn", { age: durText(Date.parse(i.expires_at) - now) })}</span>}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button size="xs" variant="outline" onClick={() => onResend(i)}><FiMail /> {t("ua.inv.resend")}</Button>
                    {i.status === "pending" && <Button size="xs" variant="outline" className="text-rose-300" disabled={revoke.isPending} onClick={() => revoke.mutate(i.id)}><FiX /> {t("ua.inv.revoke")}</Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="rounded-lg border border-rule bg-panel">
        <button type="button" onClick={() => setHistory(!history)} className="flex w-full items-center gap-3 px-4 py-2 text-left">
          <span className="eyebrow">{t("ua.inv.history")}</span>
          <span className="text-[12px] text-slate-400">{t("ua.inv.acceptedN", { n: accepted.length })}</span>
          <span className="ml-auto text-[12px] text-slate-400 hover:text-signal">{t(history ? "ua.hide" : "ua.show")}</span>
        </button>
        {history && (
          <ul className="border-t border-rule px-4 py-2 text-[12.5px]">
            {accepted.map((i) => (
              <li key={i.id} className="flex gap-3 py-1 text-slate-300">
                <span className="flex-1 truncate">{i.email}</span><RoleTag r={i.role} />
                <span className="w-40 text-right text-slate-500">{i.accepted_at && t("ua.acceptedAgo", { age: durText(now - Date.parse(i.accepted_at)) })}</span>
              </li>
            ))}
            {accepted.length === 0 && <li className="py-1 text-slate-500">—</li>}
            <li className="pt-1 text-[11.5px] text-slate-500">{t("ua.inv.historyNote")}</li>
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------- audit ----------

const CATS = ["", "role", "account", "invite", "password", "login", "session", "scope"] as const;

function EventText({ e }: { e: AccessEvent }) {
  const { t } = useI18n();
  const actor = e.actor_email || t("ua.audit.someone");
  const target = e.target || t("ua.audit.unknown");
  const p = { actor, target, from: e.from_role ? roleName(e.from_role, t) : "?", to: e.to_role ? roleName(e.to_role, t) : "", n: e.count ?? 0 };
  const key = e.action === "user.update"
    ? e.to_role ? "role" : e.disabled === false ? "enabled" : e.disabled ? "disabled" : "update"
    : e.action.replace(/^user\.|^auth\./, "").replace(/\./g, "_");
  return <>{t(`ua.ev.${key}` as Key, p)}</>;
}

function AuditTab({ users, now }: { users: PanelUser[]; now: number }) {
  const { t, locale } = useI18n();
  const [cat, setCat] = useState<(typeof CATS)[number]>("");
  const [who, setWho] = useState<number | 0>(0);
  const q = useQuery({ queryKey: ["access-audit", cat, who], queryFn: () => accessAudit({ category: cat || undefined, user: who || undefined, limit: 200 }), refetchInterval: 60_000 });
  const sorted = useMemo(() => [...users].sort((a, b) => a.email.localeCompare(b.email)), [users]);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {CATS.map((c) => (
          <button key={c || "all"} type="button" aria-pressed={cat === c} onClick={() => setCat(c)}
            className={cn("rounded-sm border px-2 py-1 text-[12px]", cat === c ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>
            {t(`ua.cat.${c || "all"}` as Key)}
          </button>
        ))}
        <Select value={String(who)} onValueChange={(v) => setWho(Number(v))}>
          <SelectTrigger className="ml-auto h-8 w-64 text-[12.5px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">{t("ua.audit.anyUser")}</SelectItem>
            {sorted.map((u) => <SelectItem key={u.id} value={String(u.id)}>{u.email}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-hidden rounded-lg border border-rule bg-panel">
        {q.isLoading && <p className="px-4 py-6 text-slate-400">{t("common.loading")}</p>}
        {q.data && q.data.events.length === 0 && <p className="px-4 py-6 text-center text-slate-500">{t("ua.audit.none")}</p>}
        <table className="w-full table-fixed text-[13px]">
          <tbody>
            {q.data?.events.map((e) => (
              <tr key={e.id} className="border-b border-rule/50 last:border-b-0">
                <td className="w-40 px-4 py-2 text-[12px] whitespace-nowrap text-slate-400" title={fmtDateTime(locale, e.at)}>{ago(e.at, now, t)}</td>
                <td className="w-28 px-2 py-2"><span className={cn("text-[10.5px] font-semibold tracking-[0.12em] uppercase",
                  e.action.endsWith(".fail") || e.action.endsWith(".denied") ? "text-signal-warn" : e.category === "role" || e.disabled ? "text-amber-200" : "text-slate-500")}>{t(`ua.cat.${e.category}` as Key)}</span></td>
                <td className="px-2 py-2 text-slate-200"><EventText e={e} /></td>
                <td className="data w-36 truncate px-4 py-2 text-right text-[11.5px] text-slate-500" title={e.user_agent}>{e.ip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11.5px] text-slate-500">{t("ua.audit.note")}</p>
    </div>
  );
}

// ---------- invite + graph access ----------

const TTL_DAYS = [1, 3, 7, 14, 30];

/**
 * Invite (and resend: a new invite for the same email replaces the old one).
 * The one-time link is shown ONCE, here: the API hashes the token, so nothing
 * can reprint it later.
 */
function InviteDialog({ initial, onClose }: { initial: { email?: string; role?: Role }; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [email, setEmail] = useState(initial.email ?? "");
  const [role, setRole] = useState<Role>(initial.role ?? "noc");
  const [ttl, setTtl] = useState(7);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const resend = !!initial.email;
  const send = useMutation({
    mutationFn: () => createInvite({ email: email.trim(), role, ttl_days: ttl }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["invites"] });
      qc.invalidateQueries({ queryKey: ["access-audit"] });
      setLink(res.invite_url ?? res.token ?? null);
    },
  });
  const can = PERMS.filter((p) => p.grant[role] !== false);
  const cannot = PERMS.filter((p) => p.grant[role] === false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t(resend ? "ua.resendTitle" : "ua.inviteTitle")}</DialogTitle>
          {resend && <DialogDescription>{t("ua.resendNote")}</DialogDescription>}
        </DialogHeader>
        {link ? (
          <div className="space-y-3">
            <p className="text-[13px] text-slate-300">{t("ua.linkOnce")}</p>
            <div className="flex gap-2">
              <Input readOnly value={link} className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
              <Button size="sm" onClick={() => { navigator.clipboard?.writeText(link); setCopied(true); }}><FiCopy /> {t(copied ? "users.copied" : "users.copy")}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block space-y-1">
              <span className="text-xs text-slate-400">{t("users.email")}</span>
              <Input type="email" autoFocus={!resend} readOnly={resend} value={email} placeholder="staf@jalabyte.com" onChange={(e) => setEmail(e.target.value)} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1">
                <span className="text-xs text-slate-400">{t("users.role")}</span>
                <Select value={role} onValueChange={(v) => setRole(v as Role)}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{roleName(r, t)}</SelectItem>)}</SelectContent>
                </Select>
              </label>
              <label className="block space-y-1">
                <span className="text-xs text-slate-400">{t("ua.expiry")}</span>
                <Select value={String(ttl)} onValueChange={(v) => setTtl(Number(v))}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>{TTL_DAYS.map((d) => <SelectItem key={d} value={String(d)}>{t("ua.days", { n: d })}</SelectItem>)}</SelectContent>
                </Select>
              </label>
            </div>
            <div className="rounded-md border border-rule px-3 py-2.5 text-[12.5px]">
              <p className="font-medium text-slate-100">{roleName(role, t)} <span className="font-normal text-slate-400">· {t(`ua.roleDesc.${role}` as Key)}</span></p>
              <div className="mt-1.5 grid grid-cols-2 gap-3">
                <div><p className="text-[10.5px] tracking-[0.12em] text-signal-good uppercase">{t("ua.can")}</p>
                  <ul className="mt-0.5 space-y-px text-slate-300">{can.map((p) => <li key={p.key}>{t(`ua.perm.${p.key}` as Key)}{p.grant[role] !== true && <span className="text-amber-200/80"> ({t(`ua.grant.${p.grant[role]}` as Key)})</span>}</li>)}</ul></div>
                <div><p className="text-[10.5px] tracking-[0.12em] text-slate-500 uppercase">{t("ua.cannot")}</p>
                  <ul className="mt-0.5 space-y-px text-slate-500">{cannot.map((p) => <li key={p.key}>{t(`ua.perm.${p.key}` as Key)}</li>)}</ul></div>
              </div>
              {role === "portviewer" && <p className="mt-2 text-[12px] text-amber-200/90">{t("ua.pvNote")}</p>}
            </div>
            {send.error && <p className="text-sm text-signal-crit">{(send.error as Error).message}</p>}
          </div>
        )}
        <DialogFooter>
          {link ? <Button onClick={onClose}>{t("common.close")}</Button> : (
            <>
              <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
              <Button disabled={!email.trim() || send.isPending} onClick={() => send.mutate()}>{t(resend ? "ua.inv.resend" : "users.sendInvite")}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Which port graphs a port-viewer account may see. Saved as a whole list, so
 * removing a graph is the same action as adding one.
 */
function GraphAccessDialog({ user, onClose }: { user: PanelUser; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const current = useQuery({ queryKey: ["user-graph-ports", user.id], queryFn: () => userGraphPorts(user.id) });
  const targets = useQuery({ queryKey: ["graph-targets", ""], queryFn: () => graphTargets("") });
  const devices: GraphTargetDevice[] = targets.data?.devices ?? [];
  const [picked, setPicked] = useState<{ port_id: number; label: string }[] | null>(null);
  const rows = picked ?? (current.data?.ports ?? []).map((p) => ({ port_id: p.librenms_port_id, label: p.label }));
  const [deviceId, setDeviceId] = useState<number | null>(null);
  const device = devices.find((d) => d.device_id === deviceId);
  const save = useMutation({
    mutationFn: () => setUserGraphPorts(user.id, rows),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user-graph-ports", user.id] });
      qc.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
  });
  const hintFor = (portID: number) => {
    for (const d of devices) {
      const p = d.ports.find((x) => x.port_id === portID);
      if (p) return `${d.name} · ${p.ifname}`;
    }
    return String(portID);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{t("users.graphAccess", { email: user.email })}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Select value={deviceId ? String(deviceId) : ""} onValueChange={(v) => setDeviceId(Number(v))}>
              <SelectTrigger className="w-64"><SelectValue placeholder={t("graphs.devicePlaceholder")} /></SelectTrigger>
              <SelectContent>{devices.map((d) => <SelectItem key={d.device_id} value={String(d.device_id)}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value="" disabled={!device} onValueChange={(v) => {
              const id = Number(v);
              if (rows.some((r) => r.port_id === id)) return;
              setPicked([...rows, { port_id: id, label: "" }]);
            }}>
              <SelectTrigger className="w-56"><SelectValue placeholder={t("graphs.portPlaceholder")} /></SelectTrigger>
              <SelectContent>{(device?.ports ?? []).map((p) => <SelectItem key={p.port_id} value={String(p.port_id)}>{p.ifname}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {rows.length === 0 && <p className="text-slate-400">{t("users.graphAccessEmpty")}</p>}
          {rows.map((r, i) => (
            <div key={r.port_id} className="flex items-center gap-2 rounded-md border border-slate-800 px-3 py-2">
              <span className="w-48 shrink-0 truncate font-mono text-xs text-slate-500">{hintFor(r.port_id)}</span>
              <Input className="flex-1" placeholder={t("shares.graphLabel")} value={r.label}
                onChange={(e) => setPicked(rows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <Button size="sm" variant="ghost" className="text-signal-crit" aria-label={t("ua.remove")} onClick={() => setPicked(rows.filter((_, j) => j !== i))}>×</Button>
            </div>
          ))}
          <p className="text-xs text-slate-500">{t("shares.customerNote")}</p>
          {save.error && <p className="text-sm text-signal-crit">{String(save.error)}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button disabled={save.isPending} onClick={() => save.mutate()}>{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
